import { round } from 'lodash';

import {
  AssociatedMetric,
  GuardrailMetric,
  Metric,
  MetricDirection,
  MetricInput,
  SuccessMetric,
} from './metricsModuleTypes';
import { MetricKind } from './types';

export const toNonDesiredDirection = (preferredDirection: MetricDirection) => {
  if (preferredDirection === MetricDirection.INCREASE) {
    return MetricDirection.DECREASE;
  }
  return MetricDirection.INCREASE;
};

export const toPreferredDirection = (nonDesiredDirection: MetricDirection) => {
  if (nonDesiredDirection === MetricDirection.INCREASE) {
    return MetricDirection.DECREASE;
  }

  return MetricDirection.INCREASE;
};

export function isGuardrailMetric(
  metric: Metric | MetricInput,
): metric is (Metric | MetricInput) & { metricRole: GuardrailMetric } {
  return (
    !!(metric.metricRole as GuardrailMetric)?.hasOwnProperty(
      'nonInferiorityMargin',
    ) ||
    (metric.metricRole as GuardrailMetric)?.metricKind === MetricKind.GUARDRAIL
  );
}

export function isSuccessMetric(
  metric: Metric | MetricInput,
): metric is (Metric | MetricInput) & { metricRole: SuccessMetric } {
  return (
    !!(metric.metricRole as SuccessMetric)?.hasOwnProperty(
      'minimumDetectableEffect',
    ) || metric.metricRole?.metricKind === MetricKind.SUCCESS
  );
}

export const formatPercentage = (x: number | undefined | null) => {
  if (x === undefined || x === null) return undefined;
  return round(x * 100, 2);
};

export const hasPlannedEffectSize = (metric: Metric) =>
  (isSuccessMetric(metric) &&
    metric.metricRole.minimumDetectableEffect !== undefined) ||
  (isGuardrailMetric(metric) &&
    metric.metricRole.nonInferiorityMargin !== undefined);

export const isRequiredMetric = (
  metric: Metric | AssociatedMetric,
): metric is Metric =>
  'metricRole' in metric && metric.metricRole !== undefined;

export const compareByDisplayName = <T>(
  itemA: T,
  itemB: T,
  getMetricName: (item: T) => string,
  getDisplayName: (metricName: string) => string | undefined,
): number => {
  const nameA = getMetricName(itemA);
  const nameB = getMetricName(itemB);
  const displayNameA = getDisplayName(nameA) || nameA;
  const displayNameB = getDisplayName(nameB) || nameB;

  return displayNameA.localeCompare(displayNameB);
};

export const sortMetricsByTypeAndName = <T>(
  items: T[] | undefined,
  getMetricType: (item: T) => string,
  getMetricName: (item: T) => string,
  getDisplayName: (metricName: string) => string | undefined,
  getMeasurementName?: (item: T) => string | undefined,
): T[] | undefined => {
  if (!items) return undefined;

  return [...items].sort((a, b) => {
    const typeA = getMetricType(a);
    const typeB = getMetricType(b);

    const getTypePriority = (type: string): number => {
      if (type === MetricKind.SUCCESS) return 0;
      if (type === MetricKind.GUARDRAIL) return 1;
      return 2;
    };

    const priorityA = getTypePriority(typeA);
    const priorityB = getTypePriority(typeB);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Within the same type group, sort by measurement so metrics
    // belonging to the same measurement are adjacent.
    if (getMeasurementName) {
      const measA = getMeasurementName(a) ?? '';
      const measB = getMeasurementName(b) ?? '';
      if (measA !== measB) {
        // Metrics with a measurement sort before those without
        if (!measA) return 1;
        if (!measB) return -1;
        const cmp = measA.localeCompare(measB);
        if (cmp !== 0) return cmp;
      }
    }

    return compareByDisplayName(a, b, getMetricName, getDisplayName);
  });
};
